<script setup lang="ts">
import type { AdminBottlePrintResponse } from '@wilson/shared'
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { api } from '../../services/api'

const route = useRoute()
const data = ref<AdminBottlePrintResponse | null>(null)
const loading = ref(true)
const error = ref('')
const settledQrIds = ref<string[]>([])
const readyToPrint = computed(
  () =>
    !loading.value &&
    Boolean(data.value?.items.length) &&
    settledQrIds.value.length === data.value?.items.length,
)

function queryString() {
  const params = new URLSearchParams()
  for (const key of ['ids', 'search', 'status', 'accessMode'] as const) {
    const value = route.query[key]
    if (typeof value === 'string' && value) params.set(key, value)
  }
  return params.toString()
}

async function load() {
  loading.value = true
  error.value = ''
  settledQrIds.value = []
  try {
    data.value = await api<AdminBottlePrintResponse>(`/admin/bottles/print?${queryString()}`)
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : 'Print data could not be loaded.'
  } finally {
    loading.value = false
  }
}

function settleQr(id: string) {
  if (!settledQrIds.value.includes(id)) settledQrIds.value = [...settledQrIds.value, id]
}

function printPage() {
  window.print()
}

onMounted(load)
</script>

<template>
  <div class="print-page">
    <header class="print-controls screen-only">
      <div>
        <RouterLink class="breadcrumb" to="/admin/bottles">← Back to bottles</RouterLink>
        <h1>Print bottle inventory</h1>
        <p v-if="data">
          {{ data.total }} bottle{{ data.total === 1 ? '' : 's' }} · QR images
          {{ readyToPrint ? 'ready' : 'loading' }}
        </p>
      </div>
      <button class="button primary" type="button" :disabled="!readyToPrint" @click="printPage">
        {{ readyToPrint ? 'Print' : 'Preparing QR codes…' }}
      </button>
    </header>

    <p v-if="error" class="form-error banner screen-only" role="alert">{{ error }}</p>
    <div v-if="loading" class="state-card screen-only">Preparing the inventory…</div>
    <div v-else-if="data && !data.items.length" class="state-card screen-only">
      No bottles match this print request.
    </div>

    <main v-if="data?.items.length" class="print-sheet" aria-label="Bottle print inventory">
      <article v-for="bottle in data.items" :key="bottle.id" class="print-bottle-card">
        <img
          :src="bottle.qrUrl"
          :alt="`QR code for ${bottle.name}`"
          @load="settleQr(bottle.id)"
          @error="settleQr(bottle.id)"
        />
        <div class="print-bottle-copy">
          <h2>{{ bottle.name }}</h2>
          <dl>
            <div>
              <dt>Quote</dt>
              <dd>{{ bottle.quoteName }}</dd>
            </div>
            <div>
              <dt>Lifecycle</dt>
              <dd>{{ bottle.status }}</dd>
            </div>
            <div>
              <dt>Access</dt>
              <dd>{{ bottle.accessMode.replace('_', ' ') }}</dd>
            </div>
            <div>
              <dt>Bottle ID</dt>
              <dd class="mono">{{ bottle.id }}</dd>
            </div>
          </dl>
          <a :href="bottle.publicUrl">{{ bottle.publicUrl }}</a>
        </div>
      </article>
    </main>
  </div>
</template>
